import { z } from 'zod';
import type { FilterGroup, SortSpec } from './filters';

/**
 * The research assistant.
 *
 * It deliberately does NOT answer questions about the data from its own reading of it — a model
 * summarizing rows would be free to invent a number nobody could check. Instead it translates a
 * plain-language request ("agencies in Cairo with email but no website") into a structured
 * filter against the whitelisted fields, the server runs that filter, and the count comes back
 * from the database. The user sees the filter that was applied and can edit it.
 *
 * That makes every answer reproducible: the same filter run twice gives the same rows, and a
 * wrong answer is visibly a wrong *filter* rather than an unverifiable claim.
 */
export const AssistantAskSchema = z.object({
  projectId: z.string().max(64).optional(),
  question: z.string().trim().min(3, 'Ask a question about your results.').max(500),
});
export type AssistantAskRequest = z.input<typeof AssistantAskSchema>;

export interface AssistantAction {
  kind: 'filter';
  filter: FilterGroup;
  sort?: SortSpec[];
  /** Plain-language restatement of what the filter does. */
  explanation: string;
}

export interface AssistantReply {
  id: string;
  question: string;
  /** What the assistant did, in plain language. */
  answer: string;
  action: AssistantAction | null;
  /** Rows the filter actually matched, counted in the database. Null when no filter was built. */
  matched: number | null;
  /** 'rules' when answered by the built-in patterns, 'gemini' when the model was used. */
  provider: 'rules' | 'gemini';
  createdAt: number;
}

export interface AssistantHistoryEntry {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: number;
}

/** Example prompts shown before the first question, so the input is never a blank mystery box. */
export const ASSISTANT_EXAMPLES: readonly string[] = [
  'businesses with an email but no website',
  'top rated in Cairo with more than 100 reviews',
  'everything missing a phone number',
  'agencies with a LinkedIn page, sorted by reviews',
];
